import React, {
  ReactElement,
  RefObject,
  useCallback,
  useMemo,
  useRef,
  useState
} from "react"
import { Box, Typography } from "@mui/material"
import {
  TableContainer,
  TableEmptyPlaceholder,
  TableFooter,
  TableHeadComponent,
  TableRowComponent
} from "./components"
import TableStyleContext from "./TableStyleContext"
import VirtualTableContext from "./VirtualTableContext"
import {
  useHeaderAndBodyScrollSync,
  useVirtualTable,
  useHasHorizontalScroll,
  useAdjustMinHeightForScroll,
  useTableLogic
} from "./hooks"
import {
  Column,
  ScrollProps,
  SortProps,
  SortTypes,
  TableRecordType
} from "./types"
import { useStyles } from "./styles"

export type SelectionProps = {
  selected?: string[]
  allSelected?: boolean
  onToggle?: (id: string) => void
  onToggleAll?: (selectAll: boolean) => void
  onDeselectAll?: () => void
}

export type CustomEmptyMessage = {
  title: string
  text: React.ReactNode
  icon?: string
  iconColor?: string
  iconBgColor?: string
}

export type ExpandableProps = {
  expandable?: boolean
  onRowExpand?: (id: string) => void
  isRowExpanded?: (id: string) => boolean
  isRowLoading?: (id: string) => boolean
}

export type TableClassesType =
  | "container"
  | "tableWrapper"
  | "table"
  | "headerTable"
  | "headerSticky"
  | "actionsSticky"
  | "firstDiagonalColumn"
  | "tableCellDiagonal"
  | "firstColumn"
  | "body"
  | "tableWrapperLoading"

export type Props = SelectionProps &
  SortProps &
  ScrollProps &
  ExpandableProps & {
    idProp?: string
    size?: "sm" | "lg"
    tableCaption?: string
    headerColumns: Column[]
    displayedProperties?: string[]
    records: TableRecordType[]
    loading?: boolean
    loadingComponent?: React.ReactNode
    emptyPlaceholder?: boolean
    className?: string
    dataAutomationId?: string
    selectable?: boolean
    renderPlaceholder?: (columns: number) => React.ReactNode
    onSelectDisplayedProperty?: (name: string) => void
    onRowClick?: (id: string, record: any, isTargetBlank?: boolean) => void
    getRowAction?: (
      id: string,
      record?: { [key: string]: string }
    ) => ReactElement
    isSelectionDisabled?: (record: TableRecordType) => boolean
    children?: (prop: string, record: TableRecordType) => React.ReactNode
    customEmptyCheck?: () => boolean
    customEmptyMessage?: CustomEmptyMessage
    classes?: Partial<Record<TableClassesType, string>>
    entityLabel?: string
    searchString?: string
    hasActiveFilters?: boolean
    tableHeight?: number
    stickyHeader?: boolean
    stickyFirstColumn?: boolean
    hideFooter?: boolean
    // container-based virtualization instead of window-based
    useContainerScroll?: boolean

    // custom renderer for selection cell (instead of default checkbox)
    renderSelectionCell?: (params: {
      record: TableRecordType
      isSelected: boolean
      disabled: boolean
      onToggle: () => void
    }) => React.ReactNode
  }

const Table: React.FC<Props> = ({
  idProp = "id",
  size = "lg",
  tableCaption,
  selected,
  allSelected,
  headerColumns,
  displayedProperties,
  records = [],
  loading,
  loadingComponent,
  emptyPlaceholder = true,
  selectable = false,
  expandable = false,
  onRowExpand,
  isRowExpanded,
  isRowLoading,
  className,
  sortBy,
  order,
  onSort,
  onSelectDisplayedProperty,
  renderPlaceholder,
  onToggle,
  onToggleAll,
  onRowClick,
  getRowAction,
  isSelectionDisabled,
  hasMoreItems,
  onFetch,
  children,
  dataAutomationId,
  classes: outerClasses,
  searchString,
  hasActiveFilters = false,
  tableHeight,
  stickyHeader = false,
  stickyFirstColumn = false,
  customEmptyCheck,
  customEmptyMessage,
  hideFooter = false,
  useContainerScroll = false,
  renderSelectionCell
}) => {
  const styles = useStyles({ size, loading })
  const { classes, cx } = styles
  const [sortAnnouncement, setSortAnnouncement] = useState<string>("")

  const headerContainerRef = useRef<HTMLDivElement>(null)

  // Use shared table logic
  const { isEmpty, totalColumnsCount, emptyPlaceholderType, dropdownOptions } =
    useTableLogic({
      records,
      loading,
      headerColumns,
      displayedProperties,
      selectable,
      getRowAction,
      hasActiveFilters,
      searchString,
      onSelectDisplayedProperty
    })

  const virtualTable = useVirtualTable({
    headerColumns,
    displayedProperties,
    records,
    size,
    sortBy,
    order,
    onSort,
    loading,
    hasMoreItems,
    onFetch,
    expandable,
    useContainerScroll
  })

  const {
    tableContainerRef,
    table,
    rowVirtualizer,
    onSort: onSortChange
  } = virtualTable

  const hasDiagonalHeaders = useMemo(() => {
    return headerColumns.some(col => col.diagonalHeader === true)
  }, [headerColumns])

  const { hasHorizontalScroll } = useHeaderAndBodyScrollSync(
    headerContainerRef,
    tableContainerRef
  )

  const hasScroll = useHasHorizontalScroll(
    tableContainerRef as RefObject<HTMLElement>,
    hasDiagonalHeaders
  )

  const sortedColumnAnnouncement = useCallback(
    (sortBy: string, order: SortTypes) => {
      const sortedColumn =
        headerColumns.find(c => (c.sortableName || c.name) === sortBy) ?? null
      const label = sortedColumn?.label || sortBy
      const sortDirectionText =
        order === SortTypes.asc ? "ascending" : "descending"

      return `${label}. Sorting ${sortDirectionText}.`
    },
    [headerColumns]
  )

  const handleSort = useCallback(
    (newSortBy: string, newOrder: SortTypes) => {
      const announcement = sortedColumnAnnouncement(newSortBy, newOrder)
      setSortAnnouncement(announcement)

      onSortChange(newSortBy, newOrder)
    },
    [onSortChange, sortedColumnAnnouncement]
  )

  useAdjustMinHeightForScroll(headerContainerRef, hasScroll, 6)

  return (
    <TableContainer styles={styles} outerClasses={outerClasses}>
      <Box
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {sortAnnouncement}
      </Box>
      <Box
        role="table"
        aria-label={tableCaption}
        className={cx(
          classes.table,
          outerClasses?.table,
          outerClasses?.headerTable
        )}
      >
        <VirtualTableContext.Provider value={virtualTable}>
          <Box
            ref={headerContainerRef}
            display={isEmpty ? "none" : "inherit"}
            className={cx(
              {
                [classes.headerSticky]: stickyHeader,
                [classes.headerStickyWithDiagonal]:
                  stickyHeader && hasDiagonalHeaders
              },
              outerClasses?.headerSticky
            )}
          >
            <Box className={classes.headerContainer}>
              <TableHeadComponent
                selected={allSelected}
                headerColumns={headerColumns}
                displayedProperties={displayedProperties}
                selectable={selectable}
                sortBy={sortBy}
                order={order}
                onSort={handleSort}
                onSelectAllRows={onToggleAll}
                getRowAction={getRowAction}
                hasDiagonalHeaders={hasDiagonalHeaders}
                stickyFirstColumn={stickyFirstColumn}
                dropdownOptions={dropdownOptions}
                hideHeaderSelection={Boolean(renderSelectionCell)}
              />
            </Box>
          </Box>

          <Box
            ref={tableContainerRef}
            className={cx(
              classes.tableWrapper,
              {
                [classes.tableWrapperLoading]: loading,
                [outerClasses?.tableWrapperLoading]: loading
              },
              outerClasses?.table,
              className
            )}
            style={
              useContainerScroll
                ? {
                    overflowY: "auto", // Required for container-based virtualization
                    overflowX: loading ? "hidden" : "auto"
                  }
                : undefined
            }
          >
            <Box
              role="rowgroup"
              data-automation-id={dataAutomationId}
              className={cx(classes.body, { [outerClasses?.body]: !isEmpty })}
              style={{
                height: isEmpty
                  ? "auto"
                  : `${tableHeight || rowVirtualizer.getTotalSize()}px`
              }}
            >
              {/* Don't sort records inside. */}
              {rowVirtualizer.getVirtualItems().map(virtualRow => {
                const row = table.getRowModel().rows[virtualRow.index]
                return (
                  <TableRowComponent
                    hasHorizontalScroll={hasHorizontalScroll}
                    key={row.id}
                    index={virtualRow.index}
                    idProp={idProp}
                    record={row.original}
                    selectedRows={selected}
                    displayedProperties={displayedProperties}
                    headerColumns={headerColumns}
                    selectable={selectable}
                    expandable={expandable}
                    onRowExpand={onRowExpand}
                    isRowExpanded={isRowExpanded}
                    isRowLoading={isRowLoading}
                    onClick={onRowClick}
                    onSelectRow={onToggle}
                    getRowAction={getRowAction}
                    hasDiagonalHeaders={hasDiagonalHeaders}
                    stickyFirstColumn={stickyFirstColumn}
                    selectDisabled={
                      isSelectionDisabled?.(row.original) ?? false
                    }
                    renderSelectionCell={renderSelectionCell}
                  >
                    {children}
                  </TableRowComponent>
                )
              })}
              <TableEmptyPlaceholder
                isEmpty={isEmpty}
                emptyPlaceholder={emptyPlaceholder}
                totalColumnsCount={totalColumnsCount}
                emptyPlaceholderType={emptyPlaceholderType}
                renderPlaceholder={renderPlaceholder}
                customEmptyCheck={customEmptyCheck}
                customEmptyMessage={customEmptyMessage}
              />
            </Box>
          </Box>
        </VirtualTableContext.Provider>
      </Box>
      {loading && loadingComponent}
      <TableFooter recordsCount={records.length} hideFooter={hideFooter} />
    </TableContainer>
  )
}

export default React.memo(Table)
